import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import { ProfileMenu } from '../Auth/ProfileMenu';
import { ChatInterfaceProps, ChatState, Message } from './types';
import { sendMessage, type ChatMessage } from '../../lib/simple-chat-client';
import { logEnvironmentInfo } from '../../lib/environmentUtils';
import { responseProcessor } from '../../lib/responseProcessor';
import { useAuth } from '../../contexts/AuthContext';
import { isAdmin } from '../../lib/admin';
import { Shield } from 'lucide-react';

const ChatInterface: React.FC<ChatInterfaceProps> = ({
  className = '',
  placeholder = "Type your message...",
  onMessageSent,
  initialMessages = [],
  onNavigateToTerms,
  onNavigateToPrivacy
}) => {
  const [chatState, setChatState] = useState<ChatState>({
    messages: initialMessages,
    isLoading: false,
    error: null,
    isStreaming: false,
    connectionStatus: 'connected',
    currentAIProvider: 'groq'
  });

  const [lastFallbackReason, setLastFallbackReason] = useState<string | null>(null);

  const { user } = useAuth();
  const navigate = useNavigate();

  // Initialize with any provided initial messages
  useEffect(() => {
    if (initialMessages.length > 0) {
      setChatState(prev => ({
        ...prev,
        messages: initialMessages
      }));
    }
    
    // Log environment info for debugging
    logEnvironmentInfo();
  }, [initialMessages]);

  const generateId = () => {
    return `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  // Count complete exchanges (Q+A pairs) for agent-friendly display
  const countCompleteExchanges = (messages: Message[]) => {
    let exchanges = 0;
    for (let i = 0; i < messages.length - 1; i += 2) {
      // Check if we have both user message and assistant response
      if (messages[i]?.role === 'user' && messages[i + 1]?.role === 'assistant') {
        // Only count if assistant response has content (complete exchange)
        if (messages[i + 1].content && messages[i + 1].content.trim().length > 0) {
          exchanges++;
        }
      }
    }
    return exchanges;
  };

  const handleSendMessage = useCallback(async (content: string, tone?: string) => {
    const userMessage: Message = {
      id: generateId(),
      role: 'user',
      content,
      timestamp: new Date()
    };

    // Add user message immediately
    setChatState(prev => ({
      ...prev,
      messages: [...prev.messages, userMessage],
      isLoading: true,
      isStreaming: false,
      error: null,
      connectionStatus: 'connecting'
    }));

    // Call external handler if provided
    if (onMessageSent) {
      onMessageSent(content);
    }

    try {
      // Convert messages to Groq format for API call
      const chatMessages: ChatMessage[] = chatState.messages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      // Add the current user message
      chatMessages.push({
        role: 'user',
        content
      });

      // Create assistant message placeholder for streaming
      const assistantMessageId = generateId();
      const assistantMessage: Message = {
        id: assistantMessageId,
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        question: content // Store the user's question for copying
      };

      // Add empty assistant message for streaming
      setChatState(prev => ({
        ...prev,
        messages: [...prev.messages, assistantMessage],
        isStreaming: true,
        connectionStatus: 'connected'
      }));

      // Use simple message API with tone
      const result = await sendMessage(chatMessages, tone);

      // 🔍 DEBUG: Log full API response
      console.log('🔍 [ChatInterface] Full API Response:', result);
      console.log('🔍 [ChatInterface] API Response Keys:', Object.keys(result));

      // Enhance response with curated prospect questions
      let enhancedMessage = result.message;
      try {
        enhancedMessage = responseProcessor.enhanceProspectQuestions(result.message);
      } catch (error) {
        console.error('[ChatInterface] Failed to enhance prospect questions:', error);
        // Use original message if enhancement fails
      }

      // Extract AI provider info from response
      const aiProvider = (result as any).ai_provider || 'groq';
      const fallbackReason = (result as any).fallback_reason || null;

      // 🔍 DEBUG: Log AI provider extraction
      console.log('🔍 [ChatInterface] Extracted AI Provider:', aiProvider === 'groq' ? 'G' : 'O');
      console.log('🔍 [ChatInterface] Extracted Fallback Reason:', fallbackReason);
      console.log('🔍 [ChatInterface] result.ai_provider raw value:', (result as any).ai_provider);
      console.log('🔍 [ChatInterface] result.fallback_reason raw value:', (result as any).fallback_reason);

      // Update state with AI provider information
      setChatState(prev => ({
        ...prev,
        messages: prev.messages.map(msg =>
          msg.id === assistantMessageId
            ? { 
                ...msg, 
                content: enhancedMessage, 
                question: content,
                aiProvider: aiProvider,
                fallbackReason: fallbackReason
              }
            : msg
        ),
        isLoading: false,
        isStreaming: false,
        connectionStatus: 'connected',
        error: result.error || null,
        currentAIProvider: aiProvider
      }));

      // Update fallback reason for the pill indicator
      setLastFallbackReason(fallbackReason);

      // 🔍 DEBUG: Log state update
      console.log('🔍 [ChatInterface] Updated currentAIProvider to:', aiProvider === 'groq' ? 'G' : 'O');
      console.log('🔍 [ChatInterface] Updated lastFallbackReason to:', fallbackReason);
      console.log('🔍 [ChatInterface] Message aiProvider set to:', aiProvider === 'groq' ? 'G' : 'O');

    } catch (error) {
      console.error('Error sending message:', error);
      setChatState(prev => ({
        ...prev,
        isLoading: false,
        isStreaming: false,
        connectionStatus: 'error',
        error: 'Failed to send message. Please check your connection and try again.'
      }));
    }
  }, [chatState.messages, onMessageSent]);

  // Listen for clicked questions from Message components
  useEffect(() => {
    const handleQuestionClick = (event: CustomEvent<{ question: string }>) => {
      const question = event.detail.question;
      if (question && !chatState.isLoading) {
        handleSendMessage(question);
      }
    };

    window.addEventListener('blp-question-click', handleQuestionClick as EventListener);
    
    return () => {
      window.removeEventListener('blp-question-click', handleQuestionClick as EventListener);
    };
  }, [chatState.isLoading, handleSendMessage]);

  return (
    <div className={`flex flex-col h-full bg-gradient-to-br from-dark-primary via-dark-secondary to-dark-tertiary ${className}`}>
      {/* BLP Header - Fixed at top */}
      <div className="flex-shrink-0 bg-dark-secondary border-b border-dark-tertiary p-3 sm:p-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3 sm:space-x-4">
            <div className="relative">
              <div className="w-3 h-3 rounded-full bg-primary-500 animate-pulse-glow"></div>
              <div className="absolute inset-0 w-3 h-3 rounded-full bg-primary-500/30 animate-ping"></div>
            </div>
            <div>
              <div className="flex items-center space-x-2 sm:space-x-3">
                <h1 className="text-lg sm:text-xl font-semibold text-light-primary">
                  BLP Assistant
                </h1>
                <div className="hidden sm:block text-xs px-3 py-1 bg-primary-500/20 text-primary-300 rounded-full border border-primary-500/30">
                  RAG-Powered
                </div>
              </div>
              <p className="text-xs sm:text-sm text-light-tertiary">
                Beneficiary Liquidity Plan® Sales Support
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2 sm:space-x-4">
            {/* Contact Information */}
            <div className="hidden lg:flex flex-col items-end text-xs text-light-tertiary space-y-1">
              <div className="text-primary-300 font-medium">Homesteaders Life</div>
              <a href="tel:800-867-9947" className="hover:text-primary-300 transition-colors flex items-center space-x-1">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
                </svg>
                <span>800-867-9947</span>
              </a>
              <a href="mailto:claimsadmin@homesteaderslife.com" className="hover:text-primary-300 transition-colors flex items-center space-x-1">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                </svg>
                <span>claimsadmin@homesteaderslife.com</span>
              </a>
            </div>
            
            <div className="hidden md:flex flex-col items-end">
              <div className="text-xs text-light-tertiary">
                {(() => {
                  const exchanges = countCompleteExchanges(chatState.messages);
                  return `${exchanges} ${exchanges === 1 ? 'exchange' : 'exchanges'}`;
                })()}
              </div>
              <div className="flex items-center space-x-1 mt-1">
                <div className={`w-2 h-2 rounded-full ${
                  chatState.connectionStatus === 'connected' ? 'bg-green-500 animate-pulse' :
                  chatState.connectionStatus === 'connecting' ? 'bg-yellow-500 animate-pulse' :
                  chatState.connectionStatus === 'error' ? 'bg-red-500' :
                  'bg-gray-500'
                }`}></div>
                <span className={`text-xs ${
                  chatState.connectionStatus === 'connected' ? 'text-green-400' :
                  chatState.connectionStatus === 'connecting' ? 'text-yellow-400' :
                  chatState.connectionStatus === 'error' ? 'text-red-400' :
                  'text-gray-400'
                }`}>
                  {chatState.connectionStatus === 'connected' ? 'Connected' :
                   chatState.connectionStatus === 'connecting' ? 'Connecting...' :
                   chatState.connectionStatus === 'error' ? 'Error' :
                   'Disconnected'}
                </span>
              </div>
            </div>

            {/* Admin Link - Only visible to admin users */}
            {user && isAdmin(user.email) && (
              <button
                onClick={() => navigate('/admin')}
                className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-primary-600/20 hover:bg-primary-600/30 text-primary-300 hover:text-primary-200 transition-all border border-primary-500/20 hover:border-primary-500/40"
                title="Admin Dashboard"
              >
                <Shield className="w-4 h-4" />
                <span className="text-sm font-medium hidden sm:inline">Admin</span>
              </button>
            )}

            {/* User Profile Menu */}
            <ProfileMenu
              onNavigateToTerms={onNavigateToTerms}
              onNavigateToPrivacy={onNavigateToPrivacy}
            />
          </div>
        </div>
      </div>

      {/* Error Display - Fixed positioning */}
      {chatState.error && (
        <div className="flex-shrink-0 max-w-4xl mx-auto w-full px-4 py-2">
          <div className="p-4 bg-red-900/30 border border-red-500/50 rounded-xl backdrop-blur-sm animate-slide-up">
            <div className="flex items-center space-x-3">
              <div className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center">
                <span className="text-white text-xs">!</span>
              </div>
              <div>
                <p className="text-red-300 text-sm font-medium">Connection Error</p>
                <p className="text-red-400 text-xs">{chatState.error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Messages Container - Scrollable full height */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full max-w-4xl mx-auto">
          <MessageList 
            messages={chatState.messages} 
            isLoading={chatState.isLoading}
            currentAIProvider={chatState.currentAIProvider}
            lastFallbackReason={lastFallbackReason}
          />
        </div>
      </div>

      {/* Input Container - Fixed at bottom */}
      <div className="flex-shrink-0 border-t border-dark-tertiary/30 bg-gradient-to-r from-dark-secondary/50 to-dark-tertiary/30 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto">
          <MessageInput
            onSendMessage={handleSendMessage}
            isLoading={chatState.isLoading}
            placeholder={placeholder}
          />
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;